import { cn } from "@faire/prototype-playground/lib/utils";
import React, { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";

import { PanelButtons } from "./PanelButtons";
import { PanelFlyout } from "./panels/PanelFlyout";
import { FlyoutPanel, StateGroup, VariantGroup } from "./types";
import { useDragControls } from "./useDragControls";
import { useKeyboardEvents } from "./useKeyboardEvents";
import { useParamsSync } from "./useParamsSync";

// Top of the dev-tools z-index hierarchy. We use one below the 32-bit signed
// max so prototypes can never accidentally outrank the toolbar by reaching for
// a "really big" zIndex (commonly 9999 / 10000). Paired with a portal to
// document.body so no parent stacking context (transform, isolation, etc.)
// can trap the toolbar inside it. If you need to render above the toolbar,
// you're almost certainly building the wrong thing — talk to design infra.
const DEV_TOOL_Z_INDEX = 2147483646;

type LiveToolbarPlacement = "left-center" | "right-center";

interface LiveToolbarProps {
  variantGroups?: VariantGroup[];
  stateGroups?: StateGroup[];
  /**
   * UI-option knobs shown in their own top-level "Settings" flyout (its own
   * toolbar button), separate from Variants so the scenario/segment stack stays
   * short. Uses the VariantGroup shape; the prototype owns persistence via
   * onChange. Omit for prototypes that don't need a Settings panel.
   */
  settingsGroups?: VariantGroup[];
  /** Default dock position before the user drags the grip. */
  defaultPlacement?: LiveToolbarPlacement;
  /** If true, the toolbar is hidden on mobile. */
  hiddenOnMobile?: boolean;
}

export const LiveToolbar: React.FC<LiveToolbarProps> = ({
  variantGroups = [],
  stateGroups = [],
  settingsGroups = [],
  defaultPlacement = "left-center",
  hiddenOnMobile = false,
} = {}) => {
  const [openPanel, setOpenPanel] = useState<FlyoutPanel>(null);
  const togglePanel = (panel: FlyoutPanel) => {
    setOpenPanel((prev) => (prev === panel ? null : panel));
  };

  const ref = useRef<HTMLDivElement>(null);
  const { dragPos, onGripPointerDown, onGripDblClick } = useDragControls(ref);

  const { isHidden } = useKeyboardEvents();

  useParamsSync(variantGroups, stateGroups);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpenPanel(null);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  if (isHidden) return null;

  // Portaled to body so the toolbar can never get trapped inside a parent
  // stacking context.
  return createPortal(
    <div
      data-faire-dev-tool="live-toolbar"
      className={cn("fixed", hiddenOnMobile && "hidden md:block")}
      style={{
        zIndex: DEV_TOOL_Z_INDEX,
        ...(dragPos
          ? { left: dragPos.x, top: dragPos.y }
          : defaultPlacement === "right-center"
            ? { right: 12, top: "50%", transform: "translateY(-50%)" }
            : { left: 12, top: "50%", transform: "translateY(-50%)" }),
      }}
      ref={ref}
    >
      <div className="flex flex-col items-center gap-1 rounded-2xl border border-[#e0e0e0]/20 bg-[#141414] p-1.5 shadow-[0_2px_8px_rgba(0,0,0,0.15)] ring-1 ring-(--slate-color-grey-1000)/5">
        <div
          onPointerDown={onGripPointerDown}
          onDoubleClick={onGripDblClick}
          className="flex h-5 w-9 cursor-grab touch-none items-center justify-center active:cursor-grabbing"
          title="Drag to reposition · double-click to reset · Shift+T to hide"
        >
          <svg width="14" height="8" viewBox="0 0 14 8" fill="none">
            <circle cx="4" cy="2" r="1.2" fill="#444" />
            <circle cx="10" cy="2" r="1.2" fill="#444" />
            <circle cx="4" cy="6" r="1.2" fill="#444" />
            <circle cx="10" cy="6" r="1.2" fill="#444" />
          </svg>
        </div>
        <PanelButtons
          hasVariants={variantGroups.length > 0}
          hasStates={stateGroups.length > 0}
          hasSettings={settingsGroups.length > 0}
          selected={openPanel}
          onSelect={togglePanel}
        />
      </div>
      <div
        className={
          dragPos && dragPos.x > window.innerWidth / 2
            ? "absolute top-0 right-[calc(100%+8px)]"
            : "absolute top-0 left-[calc(100%+8px)]"
        }
      >
        <PanelFlyout
          selected={openPanel}
          variantGroups={variantGroups}
          stateGroups={stateGroups}
          settingsGroups={settingsGroups}
        />
      </div>
    </div>,
    document.body
  );
};
